import { cn } from "./classNames-DkX30VJU.js";
import { Check, ChevronDown, ChevronUp } from "./icons-DDR9dFSr.js";
import { Button } from "./Button-DbvGzEDb.js";
import { overlayTriggerVariants } from "./styles-BXXXosW_.js";
import { forwardAccessibleName } from "./internal/utils/accessibleName.js";
import { useId } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Root } from "@radix-ui/react-select";
// src/components/Select/SelectContent.tsx
const scrollButtonStyles = cn("tw:flex", "tw:items-center", "tw:justify-center", "tw:rounded-sm", "tw:p-1", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-5");
/**
* Listbox container for `Select`. Portals items into a popover sized to the trigger.
* Normally rendered implicitly by `Select`; use directly only when composing custom item layouts via
* the `Select.Root` / `Select.Trigger` / `Select.Content` compound parts.
*/
const SelectContent = ({ ref, className, children, container, position = "popper", sideOffset = 4, ...props }) => {
	return /* @__PURE__ */ jsx(SelectPrimitive.Portal, {
		container,
		children: /* @__PURE__ */ jsxs(SelectPrimitive.Content, {
			...props,
			ref,
			position,
			sideOffset,
			className: cn("tailwindcss-preflight", "tw:z-overlay", "tw:flex tw:flex-col", "tw:p-1", "tw:overflow-hidden", "tw:origin-(--radix-select-content-transform-origin)", "tw:max-h-[var(--radix-select-content-available-height)]", "tw:min-w-[var(--radix-select-trigger-width)]", "tw:bg-popover", "tw:text-popover-foreground", "tw:shadow-md", "tw:ring-1", "tw:ring-foreground/10", "tw:rounded-lg", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=open]:zoom-in-95", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=closed]:zoom-out-95", "tw:data-[side=bottom]:slide-in-from-top-2", "tw:data-[side=left]:slide-in-from-right-2", "tw:data-[side=right]:slide-in-from-left-2", "tw:data-[side=top]:slide-in-from-bottom-2", className),
			children: [
				/* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, {
					className: scrollButtonStyles,
					children: /* @__PURE__ */ jsx(ChevronUp, {})
				}),
				/* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
					className: cn({ "tw:h-[var(--radix-select-trigger-height)] tw:w-full tw:min-w-[var(--radix-select-trigger-width)]": position === "popper" }),
					children
				}),
				/* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, {
					className: scrollButtonStyles,
					children: /* @__PURE__ */ jsx(ChevronDown, {})
				})
			]
		})
	});
};
// src/components/Select/SelectGroup.tsx
const SelectGroup = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Group, {
	...props,
	className: cn("tw:flex tw:flex-col tw:gap-0.5", className)
});
// src/components/Select/SelectItem.tsx
const SelectItem = ({ ref, className, children, icon, hint, "aria-describedby": ariaDescribedBy, ...props }) => {
	const hintId = useId();
	return /* @__PURE__ */ jsxs(SelectPrimitive.Item, {
		...props,
		ref,
		"aria-describedby": [ariaDescribedBy, hint ? hintId : null].filter(Boolean).join(" ") || void 0,
		className: cn("tw:group/select-item", "tw:relative", "tw:flex", "tw:flex-1", "tw:items-center", "tw:gap-2", "tw:py-2", "tw:px-2", "tw:rounded-lg", "tw:text-sm", "tw:select-none", "tw:cursor-default", "tw:outline-hidden", "tw:focus:outline-hidden", "tw:data-[highlighted]:bg-hover", "tw:data-[highlighted]:text-hover-foreground", "tw:data-[disabled]:pointer-events-none", "tw:data-[disabled]:opacity-disabled", "tw:[&_svg]:pointer-events-none", "tw:[&_svg]:shrink-0", "tw:[&_svg]:text-muted-foreground", "tw:[&_svg:not([class*='tw:size-'])]:size-4", className),
		children: [
			icon,
			/* @__PURE__ */ jsx("span", {
				className: "tw:flex-1 tw:truncate",
				children: /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
			}),
			hint && /* @__PURE__ */ jsx("span", {
				id: hintId,
				className: "tw:shrink-0 tw:text-xs tw:text-muted-foreground",
				children: hint
			}),
			/* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, {
				className: "tw:pointer-events-none",
				children: /* @__PURE__ */ jsx(Check, { className: "tw:size-4" })
			})
		]
	});
};
// src/components/Select/SelectLabel.tsx
const SelectLabel = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Label, {
	...props,
	className: cn("tw:px-2", "tw:py-1.5", "tw:text-xs", "tw:font-medium", "tw:leading-none", "tw:text-muted-foreground", className)
});
// src/components/Select/SelectRoot.tsx
const SelectRoot = (props) => /* @__PURE__ */ jsx(Root, { ...props });
// src/components/Select/SelectSeparator.tsx
const SelectSeparator = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectPrimitive.Separator, {
	...props,
	className: cn("tw:mx-2 tw:my-1 tw:h-px tw:bg-border", className)
});
// src/components/Select/SelectTrigger.tsx
const SelectTrigger = ({ ref, className, inline, state, placeholder, ...props }) => {
	if (inline) return /* @__PURE__ */ jsx(SelectPrimitive.Trigger, {
		...props,
		ref,
		className: cn(className),
		asChild: true,
		children: /* @__PURE__ */ jsxs(Button, {
			variant: "inline",
			children: [/* @__PURE__ */ jsx(SelectPrimitive.Value, { placeholder }), /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
				asChild: true,
				children: /* @__PURE__ */ jsx(ChevronDown, {})
			})]
		})
	});
	return /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
		...props,
		ref,
		className: cn(overlayTriggerVariants({ state }), className),
		children: [/* @__PURE__ */ jsx(SelectPrimitive.Value, { placeholder }), /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
			asChild: true,
			children: /* @__PURE__ */ jsx(ChevronDown, { className: "tw:size-4 tw:text-muted-foreground" })
		})]
	});
};
// src/components/Select/Select.tsx
const isGroup = (itemOrGroup) => "items" in itemOrGroup;
/**
* Radix's value for an item whose `value` is `''`.
*
* - Radix reads `''` as "no value" and shows the placeholder, even when an item holds it.
* - Swapped in only when such an item exists; otherwise `''` still shows the placeholder.
* - With `name` in a natively submitted form, the hidden `<select>` sends it and passes `required`.
*/
const EMPTY_ITEM_VALUE = "__select-empty-item__";
/**
* Single selection from a short enumerated list. Built on Radix Select.
*
* - Use for ~10 or fewer options where search adds nothing; else `Combobox` / `MultiCombobox`.
* - In forms: `FormGroup`, or `useAppForm` + `field.SelectField` for validation.
* - Compound parts (`Select.Root`, `.Trigger`, `.Content`, `.Item`) for custom item layouts.
* - An item may hold `''` (an "All" filter): selected, it shows its label, not the placeholder.
*/
const Select = ({ value, defaultValue, onValueChange, disabled, name, required, open, defaultOpen, onOpenChange, dir, autoComplete, form, inline, state, placeholder, className, onBlur, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-required": ariaRequired, items = [], ...accessibleName }) => {
	const groupKeyId = useId();
	const hasEmptyItem = items.some((itemOrGroup) => isGroup(itemOrGroup) ? itemOrGroup.items.some((item) => item.value === "") : itemOrGroup.value === "");
	const toRootValue = (rawValue) => hasEmptyItem && rawValue === "" ? EMPTY_ITEM_VALUE : rawValue;
	const renderItem = (item) => /* @__PURE__ */ jsx(SelectItem, {
		value: toRootValue(item.value),
		icon: item.icon,
		hint: item.hint,
		disabled: item.disabled,
		children: item.label
	}, item.value);
	return /* @__PURE__ */ jsxs(SelectRoot, {
		value: toRootValue(value),
		defaultValue: toRootValue(defaultValue),
		onValueChange: onValueChange && ((rootValue) => onValueChange(rootValue === EMPTY_ITEM_VALUE ? "" : rootValue)),
		disabled,
		name,
		required,
		open,
		defaultOpen,
		onOpenChange,
		dir,
		autoComplete,
		form,
		children: [/* @__PURE__ */ jsx(SelectTrigger, {
			...forwardAccessibleName(accessibleName),
			inline,
			state,
			placeholder,
			className,
			onBlur,
			"aria-describedby": ariaDescribedBy,
			"aria-invalid": ariaInvalid,
			"aria-required": ariaRequired
		}), /* @__PURE__ */ jsx(SelectContent, { children: items.map((itemOrGroup, index) => {
			if (isGroup(itemOrGroup)) return /* @__PURE__ */ jsxs(SelectGroup, { children: [itemOrGroup.label && /* @__PURE__ */ jsx(SelectLabel, { children: itemOrGroup.label }), itemOrGroup.items.map(renderItem)] }, `${groupKeyId}-group-${index}`);
			return renderItem(itemOrGroup);
		}) })]
	});
};
Select.Root = SelectRoot;
Select.Trigger = SelectTrigger;
Select.Content = SelectContent;
Select.Item = SelectItem;
Select.Group = SelectGroup;
Select.Label = SelectLabel;
Select.Separator = SelectSeparator;
export { Select };

//# sourceMappingURL=Select-DRdkdzBp.js.map